import { useEffect, useRef, useState } from 'react'

export interface UseFilePicker {
    inputRef: React.RefObject<HTMLInputElement | null>
    /** Object URL of the most recently picked file (revoked on release). */
    pickedSrc: string | null
    /** Programmatically open the hidden file input. */
    open: () => void
    /** Wire to the hidden input's `onChange`. */
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    /** Drop the current `pickedSrc` and revoke its object URL. */
    release: () => void
}

/**
 * Manages a hidden `<input type="file">` and an object URL for the picked file.
 * Resets the input value on each pick so the same file can be selected twice.
 */
export const useFilePicker = (): UseFilePicker => {
    const inputRef = useRef<HTMLInputElement>(null)
    const pickedSrcRef = useRef<string | null>(null)
    const [pickedSrc, setPickedSrc] = useState<string | null>(null)

    useEffect(() => {
        return () => {
            if (pickedSrcRef.current) URL.revokeObjectURL(pickedSrcRef.current)
        }
    }, [])

    const release = () => {
        if (pickedSrcRef.current) {
            URL.revokeObjectURL(pickedSrcRef.current)
            pickedSrcRef.current = null
        }
        setPickedSrc(null)
    }

    const open = () => inputRef.current?.click()

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return

        release()
        const url = URL.createObjectURL(file)
        pickedSrcRef.current = url
        setPickedSrc(url)
    }

    return { inputRef, pickedSrc, open, handleChange, release }
}
